import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import './select-fix.css';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ClientDetails from './pages/ClientDetails';
import Prospects from './pages/Prospects';
import EmailTemplates from './pages/EmailTemplates';
import Settings from './pages/Settings';
import UsersSettings from './pages/UsersSettings';
import AgentInstaller from './pages/AgentInstaller';
import IntegrationsSettings from './pages/IntegrationsSettings';
import ClientConfiguration from './pages/ClientConfiguration';
import ScreenshotSettings from './pages/ScreenshotSettings';
import DevelopmentResources from './pages/DevelopmentResources';
import AssetAlertsSettings from './pages/AssetAlertsSettings';
import AISettings from './pages/AISettings';
import RegionalSettings from './pages/RegionalSettings';
import PricingSettings from './pages/PricingSettingsNew';
import TermsSettings from './pages/TermsSettings';
import MonitorAlertSettings from './pages/MonitorAlertSettings';
import GenerateQuote from './pages/GenerateQuote';
import QuoteView from './pages/QuoteView';
import PublicQuoteView from './pages/PublicQuoteView';
import Alerts from './pages/Alerts';
import RemoteControlViewer from './pages/RemoteControlViewer';

// Public pages
import LandingPage from './pages/public/LandingPage';
import PricingPage from './pages/public/PricingPage';
import FeaturesPage from './pages/public/FeaturesPage';
import AboutPage from './pages/public/AboutPage';
import ResourcesPage from './pages/public/ResourcesPage';
import RoadmapPage from './pages/public/RoadmapPage';
import ChangelogPage from './pages/public/ChangelogPage';
import DemoPage from './pages/public/DemoPage';
import SignupPage from './pages/SignupPage';
import NewCompanies from './pages/NewCompanies';
import ComposeEmail from './pages/ComposeEmail';
import Contracts from './pages/Contracts';
import ContractTemplates from './pages/ContractTemplates';
import NewContract from './pages/NewContract';
import ContractDetail from './pages/ContractDetail';
import EditContract from './pages/EditContract';
import EditTemplate from './pages/EditTemplate';
import SignContract from './pages/SignContract';
import Requests from './pages/Requests';
import NewRequest from './pages/NewRequest';
import RequestDetail from './pages/RequestDetail';
import AdminDashboard from './pages/AdminDashboard';
import PlatformAdmin from './pages/PlatformAdmin';
import PurchaseCredits from './pages/PurchaseCredits';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import UptimeMonitor from './pages/UptimeMonitor';
import MonitorDetails from './pages/MonitorDetails';
import MonitorHistory from './pages/MonitorHistory';
import HypervisorMonitoring from './pages/HypervisorMonitoring';
import AssetsRMM from './pages/AssetsRMM';
import AssetDetail from './pages/AssetDetail';
import AssetDetailTabs from './pages/AssetDetailTabs';
import KnowledgeBase from './pages/KnowledgeBase';
import UniFiControllers from './pages/UniFiControllers';
import NonAgentAssets from './pages/NonAgentAssets';
import DashboardLayout from './components/DashboardLayout';
import { Toaster } from './components/ui/sonner';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Marketing Pages */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/public/pricing" element={<PricingPage />} />
          <Route path="/public/features" element={<FeaturesPage />} />
          <Route path="/public/about" element={<AboutPage />} />
          <Route path="/public/resources" element={<ResourcesPage />} />
          <Route path="/public/roadmap" element={<RoadmapPage />} />
          <Route path="/public/changelog" element={<ChangelogPage />} />
          <Route path="/public/demo" element={<DemoPage />} />
          

          {/* Public Quote View - No Auth Required */}
          <Route path="/public/quote/:quoteId/:token" element={<PublicQuoteView />} />
          
          {/* Public Contract Signing - No Auth Required */}
          <Route path="/contracts/sign/:token" element={<SignContract />} />

          {/* Authentication */}
          <Route path="/signup" element={<SignupPage />} />
          <Route
            path="/login"
            element={!isAuthenticated ? <Login setAuth={setIsAuthenticated} /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/register"
            element={!isAuthenticated ? <Register setAuth={setIsAuthenticated} /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Dashboard setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/companies"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Dashboard setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/clients/:id"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <ClientDetails setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/prospects"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Prospects />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/generate-quote/:prospectId"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <GenerateQuote />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/quotes/:quoteId"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <QuoteView />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/email-templates"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <EmailTemplates setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Settings setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/users"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <UsersSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/agent-installer"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <AgentInstaller />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/integrations"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <IntegrationsSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/unifi-controllers"
            element={
              isAuthenticated ? (
                <UniFiControllers />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/client-configuration"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <ClientConfiguration />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/screenshots"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <ScreenshotSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/development-resources"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <DevelopmentResources />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/asset-alerts"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <AssetAlertsSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/ai"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <AISettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/regional"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <RegionalSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/settings/pricing"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <PricingSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings/terms"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <TermsSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings/monitor-alerts"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <MonitorAlertSettings />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/alerts"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Alerts />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/new-companies"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <NewCompanies setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/compose-email"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <ComposeEmail />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <AdminDashboard setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/platform-admin"
            element={
              isAuthenticated ? (
                <PlatformAdmin />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Contracts />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/templates"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <ContractTemplates />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/templates/:id/edit"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <EditTemplate />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/new"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <NewContract />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/:id"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <ContractDetail />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/:id/edit"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <EditContract />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/requests"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Requests />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/requests/new"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <NewRequest />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/requests/:id"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <RequestDetail />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/purchase-credits"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <PurchaseCredits setAuth={setIsAuthenticated} />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/tickets"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <Tickets />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/tickets/:id"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <TicketDetail />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/uptime"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <UptimeMonitor />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/uptime/:id"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <MonitorDetails />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/uptime/:id/history"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <MonitorHistory />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/hypervisors"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <HypervisorMonitoring />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/assets"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <AssetsRMM />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/network-assets"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <NonAgentAssets />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/assets/:id"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <AssetDetailTabs />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/knowledge"
            element={
              isAuthenticated ? (
                <DashboardLayout setAuth={setIsAuthenticated}>
                  <KnowledgeBase />
                </DashboardLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/remote-control/:sessionId"
            element={
              isAuthenticated ? (
                <RemoteControlViewer />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" expand={false} richColors />
    </div>
  );
}

export default App;
